// src/components/Chat/Message/MessageChart.tsx
import React, { useState } from "react";
import { Box, Typography, Collapse, Paper, IconButton } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LeaderboardOutlinedIcon from "@mui/icons-material/LeaderboardOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

import BarChartComponent from "./Chart/BarChartComponent";
import LineChartComponent from "./Chart/LineChartComponent";
import AreaChartComponent from "./Chart/AreaChartComponent";
import PieChartComponent from "./Chart/PieChartComponent";
import ScatterChartComponent from "./Chart/ScatterChartComponent";

const MessageChart: React.FC<{
  sender: any;
  chart_data: {
    plot: boolean;
    data: Array<{ [key: string]: any }>;
    chart_name: string;
    visualization_type: string;
  };
  theme: any;
}> = ({ sender, chart_data, theme }) => {
  const [showChart, setShowChart] = useState<boolean>(true);

  if (!chart_data.plot) {
    return null; // If 'plot' is false, don't render the chart
  }

  const detectAxes = () => {
    const sampleItem = chart_data.data[0] || {};
    const keys = Object.keys(sampleItem);

    const labelKey = keys.find((key) => typeof sampleItem[key] === "string");
    const numericKeys = keys.filter(
      (key) => typeof sampleItem[key] === "number"
    );

    return { labelKey, numericKeys };
  };

  const { labelKey, numericKeys } = detectAxes();

  if (!labelKey || numericKeys.length === 0) {
    return null;
  }

  const chartData = chart_data.data.map((item) => {
    const transformedItem: any = {
      label: item[labelKey], // Use labelKey for labels
    };

    numericKeys.forEach((key) => {
      transformedItem[key] = item[key]; // Add numeric values dynamically
    });

    return transformedItem;
  });

  // Render the appropriate chart
  const renderChart = () => {
    switch (chart_data.visualization_type) {
      case "bar":
        return (
          <BarChartComponent
            data={chartData}
            name={chart_data.chart_name}
            theme={theme}
          />
        );
      case "line":
        return (
          <LineChartComponent
            data={chartData}
            name={chart_data.chart_name}
            theme={theme}
          />
        );
      case "area":
        return (
          <AreaChartComponent
            data={chartData}
            name={chart_data.chart_name}
            theme={theme}
          />
        );
      case "pie":
        return (
          <PieChartComponent
            data={chartData}
            name={chart_data.chart_name}
            theme={theme}
          />
        );
      case "scatter":
        return (
          <ScatterChartComponent
            data={chartData}
            name={chart_data.chart_name}
            theme={theme}
          />
        );
      default:
        return <Typography variant="body1">Unsupported chart type.</Typography>;
    }
  };

  return (
    <Paper variant="outlined" sx={{ bgcolor: "background.default" }}>
      <Box
        sx={{
          p: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
        }}
        onClick={() => setShowChart(!showChart)}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LeaderboardOutlinedIcon sx={{ fontSize: 16 }} />
          <Typography variant="subtitle2">แผนภูมิข้อมูล</Typography>
        </Box>
        <IconButton size="small" edge="end">
          {showChart ? (
            <ExpandLessIcon sx={{ fontSize: 16 }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      </Box>
      <Collapse in={showChart}>
        <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
          {chartData.length > 0 ? (
            <Box>
              {renderChart()}
              <Box
                sx={{
                  width: "100%",
                  mt: 2,
                  p: 1,
                  borderRadius: "4px",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "background.paper",
                }}
              >
                <WarningAmberOutlinedIcon
                  sx={{ fontSize: "16px", mr: 1, color: "text.secondary" }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center" }}
                >
                  แผนภูมิอาจมีการแสดงผลผิดพลาดได้
                  โปรดตรวจสอบความถูกต้องก่อนนำไปใช้งานทุกครั้ง
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography variant="body1">Invalid chart data format.</Typography>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default MessageChart;
